import { bool, func, string } from "prop-types";
import { useState } from "react";

import styled from "styled-components/macro";

import FnrButton from "UI/FnrButton3";
import FnrContainedIcon from "UI/FnrContainedIcon";
import FnrTypography from "UI/FnrTypography";

// Popover body: heading/subtext with the action button in the lower right.
// The popover shell chrome (frosted white, shadow, radius) comes from
// FnrPopover via the hosting FnrButtonPopper's `color="white"` — this
// component supplies only content.
const TogglePopover = ({ verified, onToggle, verifiedByName, verifiedBySelf }) => {
  // Latch the state as of open. The toggle's optimistic flip re-renders this
  // content during the popover's exit animation, which used to flash the
  // OTHER state's copy for a beat before close. FnrPopover unmounts content
  // when closed, so the latch resets naturally on the next open.
  const [latchedVerified] = useState(verified);

  // First-person copy when the current user is the verifier; otherwise credit
  // them by name (or the generic fallback).
  const verifier = verifiedBySelf ? "You" : verifiedByName || "Someone here";

  return (
    <PopoverWrapper role="region" aria-label="address-verification">
      <HeaderText>
        <FnrTypography
          fontSize="sm"
          fontWeight={600}
          color="textSecondaryDark"
          lineHeight={1.3}
        >
          {latchedVerified ? "Verified by an Admin" : "Manually Verify"}
        </FnrTypography>
        <FnrTypography fontSize=".75em" color="textSecondary" lineHeight={1.35}>
          {latchedVerified
            ? `${verifier} manually verified this address. You can undo it anytime.`
            : "If you're confident this address is correct, you can manually verify it as an admin. A badge will indicate admin verification rather than postal service verification."}
        </FnrTypography>
      </HeaderText>
      <ButtonRow>
        <FnrButton
          type="button"
          color="blue"
          size="small"
          onClick={onToggle}
          role="switch"
          aria-label="toggle address verification"
          aria-checked={latchedVerified}
        >
          <ButtonContent>
            <FnrContainedIcon
              icon={latchedVerified ? "undo fas" : "check fas"}
              size="sm"
              noBackgroundColor
              noPadding
              color="currentColor"
            />
            {latchedVerified ? "Unverify" : "Verify"}
          </ButtonContent>
        </FnrButton>
      </ButtonRow>
    </PopoverWrapper>
  );
};
TogglePopover.propTypes = {
  verified: bool,
  onToggle: func,
  verifiedByName: string,
  verifiedBySelf: bool,
};

export default TogglePopover;

const PopoverWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 240px;
  padding: 4px 2px;
`;

// Flex column so the FnrTypography children are blockified and own their line
// boxes (see FRONTEND_PATTERNS.md "FnrTypography Must Own Its Line Box").
const HeaderText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
